import React, { useState, useRef, useCallback, useEffect } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VoiceInputButton = ({ fieldType, onResult, disabled }) => {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const onResultRef = useRef(onResult);
  const fieldTypeRef = useRef(fieldType);

  // Keep refs in sync with latest props
  useEffect(() => { onResultRef.current = onResult; }, [onResult]);
  useEffect(() => { fieldTypeRef.current = fieldType; }, [fieldType]);

  // Cleanup on unmount — kill stream + timer no matter what
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current?.state === "recording") {
        try { mediaRecorderRef.current.stop(); } catch (e) {
          console.warn("MediaRecorder cleanup failed:", e);
        }
      }
    };
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      setDuration(0);

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        clearInterval(timerRef.current);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 1000) {
          toast.error("Aufnahme zu kurz. Bitte erneut versuchen.");
          setRecording(false);
          return;
        }
        setRecording(false);
        setProcessing(true);
        try {
          const formData = new FormData();
          formData.append("audio", blob, "recording.webm");
          formData.append("field_type", fieldTypeRef.current);
          const res = await axios.post(`${API}/voice/transcribe`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000,
          });
          onResultRef.current(res.data.processed, res.data.transcript);
          toast.success("Spracheingabe verarbeitet!");
        } catch (err) {
          console.error("Voice error:", err);
          const msg = err.response?.data?.detail || "Spracherkennung fehlgeschlagen";
          toast.error(msg);
        } finally {
          setProcessing(false);
        }
      };

      mediaRecorder.start(1000);
      setRecording(true);
      timerRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
    } catch (err) {
      if (err.name === "NotAllowedError") {
        toast.error("Mikrofon-Zugriff verweigert. Bitte in den Browser-Einstellungen erlauben.");
      } else {
        toast.error("Mikrofon nicht verfügbar");
      }
    }
  }, []); // Refs are stable, no deps needed

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  if (processing) {
    return (
      <Button variant="outline" size="sm" disabled className="gap-2 text-xs">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Verarbeite...
      </Button>
    );
  }

  if (recording) {
    return (
      <Button
        variant="destructive"
        size="sm"
        onClick={stopRecording}
        className="gap-2 text-xs animate-pulse"
        data-testid="voice-stop-btn"
      >
        <Square className="h-3 w-3 fill-current" />
        Stop ({formatTime(duration)})
      </Button>
    );
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={startRecording}
      disabled={disabled}
      className="gap-2 text-xs hover:bg-primary/10 hover:border-primary"
      title="Einsprechen statt tippen"
      data-testid="voice-record-btn"
    >
      <Mic className="h-3.5 w-3.5" />
      Einsprechen
    </Button>
  );
};
